import React, { useEffect } from 'react';
import { Button, Table } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import debounce from 'packages/helpers/debounce';
import { LoadingLayer } from '../grid/Grid.styled';
import { StickyHack } from './List.styled';
import { Props } from './types';
import { InfinityLoader } from 'packages/pages/components/InfinityLoader/InfinityLoader';

const DEFAULT_THRESHOLD = 250;
const DEFAULT_SCROLLING_RESET_TIME_INTERVAL = 150;

/**
 * ListView component supporting two scroll detection mechanisms.
 *
 * TODO / DEPRECATION NOTE:
 * Ideally, all consumers should migrate to using the 'intersection' scrollMode (IntersectionObserver).
 * The legacy 'scroll' mode is kept temporarily for backward compatibility while migration is in progress.
 */
const ListView = ({
    columnWidths,
    renderHeader,
    renderItem,
    isLoading,
    container,
    onScroll,
    scrollThreshold = DEFAULT_THRESHOLD,
    // Defaulting to legacy 'scroll' mode to maintain backward compatibility for existing consumers
    scrollMode = { type: 'scroll' },
}: Props) => {
    const { translate } = useTranslate();

    const handleOnScroll = debounce(
        () => {
            if (container?.current) {
                const { clientHeight, scrollTop, scrollHeight } = container.current;

                if (clientHeight + scrollTop >= scrollHeight - scrollThreshold) {
                    onScroll();
                }
            }
        },
        DEFAULT_SCROLLING_RESET_TIME_INTERVAL,
        false,
    );

    useEffect(() => {
        if (scrollMode.type !== 'scroll') {
            return;
        }

        const containerCurrent = container?.current;

        if (containerCurrent) {
            containerCurrent.addEventListener('scroll', handleOnScroll, false);
        }

        return () => {
            if (containerCurrent) {
                containerCurrent.removeEventListener('scroll', handleOnScroll, false);
            }
        };
    }, [container, handleOnScroll, scrollMode.type]);

    return (
        <>
            <StickyHack />
            <Table columnWidths={columnWidths}>
                <Table.Head stickyOffset="0px">{renderHeader}</Table.Head>
                <Table.Body>{renderItem}</Table.Body>
            </Table>

            {/* Legacy scroll mode loading indicator */}
            {isLoading && scrollMode.type === 'scroll' && (
                <LoadingLayer data-testid="list view loader">
                    <Button isLoading variant="clean" aria-label={translate('pages.loading')} />
                </LoadingLayer>
            )}

            {/* Preferred modern mode using IntersectionObserver */}
            {scrollMode.type === 'intersection' && (
                <InfinityLoader
                    isLoading={scrollMode.isLoading}
                    count={scrollMode.count}
                    totalItems={scrollMode.totalItems}
                    loadMore={scrollMode.loadMore}
                />
            )}
        </>
    );
};

export default ListView;
